import { userCoursePerformanceId, type Course } from '@webapp2/shared/model'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { router, type DebriefRouteLocation } from '../router'
import { prebriefId, registeredStateId, state, type BuildDownloadStatus } from '../state'
import { techniqueDetailsByTechniquePath, type TechniqueDetails } from './build-service'
import { registerModules } from './quest-bridge'

export type CourseDownloadInfo = {
  downloadStatus: BuildDownloadStatus
  buildOverride: TechniqueDetails
  course: Course
}

export function resolvePrebriefUrl(course: Course) {
  return (
    window.location.origin +
    router.resolve({ name: 'prebrief', params: { organizationId: state.organizationId, courseId: course.id } }).href
  )
}

export async function startCourse(course: Course) {
  await router.push({
    name: 'prebrief',
    params: {
      organizationId: state.organizationId,
      courseId: course.id
    }
  })
}

export function useCourseRoute() {
  const route = useRoute() as DebriefRouteLocation
  const courseId = route.params.courseId ?? state.runResults[route.params.runId]?.courseId
  const course = computed(() => state.courses[courseId]!)
  const prebrief = computed(() => state.prebriefs[prebriefId(course.value?.techniqueId)]!)
  const playerTechnique = computed(() => state.playerTechniques[course.value?.techniqueId]!)
  const techniqueModuleBuildDetails = computed(() => techniqueDetailsByCourseId.value[courseId]!)
  const moduleBuild = computed(
    () => techniqueModuleBuildDetails.value?.moduleBuild ?? techniqueModuleBuildDetails.value.courseBuild
  )
  const userCoursePerformance = computed(
    () => state.userCoursePerformances[userCoursePerformanceId(state.organizationId!, course.value.id, state.user!.id)]
  )
  return {
    route,
    course,
    prebrief,
    moduleBuild,
    playerTechnique,
    techniqueModuleBuildDetails,
    userCoursePerformance
  }
}

export const coursesByName = computed(() => Object.values(state.courses).sort((a, b) => a.name.localeCompare(b.name)))

export const courseByTechniquePath = computed(() =>
  Object.values(state.moduleBuilds).reduce<{ [techniquePath: string]: Course }>((acc, build) => {
    const course = Object.values(state.courses).find(x => x.buildId === build.id)
    if (course) {
      acc[build.techniquePath] = course
    }
    return acc
  }, {})
)

export const techniqueDetailsByCourseId = computed(() =>
  Object.values(state.courses).reduce<{ [courseId: string]: TechniqueDetails }>((acc, course) => {
    acc[course.id] = techniqueDetailsByTechniquePath.value[state.moduleBuilds[course.buildId]!.techniquePath]!
    return acc
  }, {})
)

export function registerModuleBuildForCourse(courseId: string) {
  const course = state.courses[courseId]!
  const moduleBuild = state.moduleBuilds[course.buildId]!

  if (
    !state.registeredStates[registeredStateId(moduleBuild.techniquePath)]?.inMemory &&
    !techniqueDetailsByCourseId.value[courseId]?.embeddedModule
  ) {
    registerModules({ builds: [moduleBuild] })
  }
}

export async function goToCourse(course: Course) {
  await router.push({
    name: 'prebrief-simulation',
    params: { organizationId: state.organizationId, courseId: course.id }
  })
}
